import type { SerializedEditorState } from "lexical";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";

import { NOTE_NODES, notesOnError, notesTheme } from "../notesConfig";
import { storedLocale } from "../../../../Authentication/util";
import "./NotesViewer.scss";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../../content/downlineProfile";

// Formats a save timestamp to match the design, e.g. "02/12/26, 10:20 AM".
export const formatLastEdit = (timestamp: number): string => {
  return new Intl.DateTimeFormat(storedLocale(), {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).format(new Date(timestamp));
};

type Props = {
  content: SerializedEditorState;
  lastEdited: number | null;
  onEdit: () => void;
  onDelete: () => void;
  actionsDisabled?: boolean;
};

// Renders a saved note (serialized Lexical state) read-only, reusing the same
// theme so the formatting matches exactly what was entered in the editor.
export const NotesViewer = ({ content, lastEdited, onEdit, onDelete, actionsDisabled = false }: Props) => {
  const { t } = useTranslation(["downlineProfile"]);
  // LexicalComposer reads initialConfig only on mount, so a changed `content`
  // prop won't update an already-mounted editor. Serialize once and use it as
  // the composer key so a new note state forces a fresh mount.
  const serializedContent = JSON.stringify(content);
  const viewerConfig = {
    namespace: "NotesViewer",
    nodes: NOTE_NODES,
    theme: notesTheme,
    onError: notesOnError,
    editable: false,
    editorState: serializedContent,
  };

  return (
    <div className="NotesViewer">
      {lastEdited !== null && (
        <p className="NotesViewer__timestamp">
          {t("notes.noteLastEdit", defaultContent.notes.noteLastEdit, {
            timestamp_MMDDYY_HHMMSS: formatLastEdit(lastEdited),
          })}
        </p>
      )}

      <LexicalComposer key={serializedContent} initialConfig={viewerConfig}>
        <RichTextPlugin
          contentEditable={<ContentEditable className="NotesViewer__content" />}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </LexicalComposer>

      <div className="NotesViewer__links">
        <button type="button" className="NotesViewer__link" onClick={onEdit} disabled={actionsDisabled}>
          {t("notes.edit", defaultContent.notes.edit as string)}
        </button>
        <button type="button" className="NotesViewer__link" onClick={onDelete} disabled={actionsDisabled}>
          {t("notes.delete", defaultContent.notes.delete as string)}
        </button>
      </div>
    </div>
  );
};
